import './styles.scss'
import { toTwoDigits } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

export interface TimePickerProps {
  epoch: number
  minEpochValue: number
  maxEpochValue: number
  disabled?: boolean
  label?: string
  onTimeChanged: (time: number) => void
}

export const TimePicker = ({ epoch, minEpochValue, maxEpochValue, disabled, label, onTimeChanged }: TimePickerProps) => {
  const [isFocused, setFocused] = useState(false)
  const [hours, setHours] = useState('')
  const [minutes, setMinutes] = useState('')
  const [seconds, setSeconds] = useState('')

  const date: Date = useMemo(() => new Date(epoch * 1000), [epoch])

  const resetTime = useCallback(() => {
    setHours(toTwoDigits(date.getUTCHours()))
    setMinutes(toTwoDigits(date.getUTCMinutes()))
    setSeconds(toTwoDigits(date.getUTCSeconds()))
  }, [setHours, setMinutes, setSeconds, date])

  useEffect(() => {
    resetTime()
  }, [epoch, resetTime])

  const debouncedHandleTimeChange = useDebouncedCallback(async (hrs: number, mins: number, secs: number) => {
    if (mins > 60 || mins < 0) {
      resetTime()
      return
    }
    if (secs > 60 || secs < 0) {
      resetTime()
      return
    }

    const selectedTime = minEpochValue + hrs * 3600 + mins * 60 + secs
    if (selectedTime > maxEpochValue || selectedTime < minEpochValue) {
      resetTime()
      return
    }

    onTimeChanged(selectedTime)
  }, 500)

  useEffect(() => {
    debouncedHandleTimeChange(Number(hours), Number(minutes), Number(seconds))
  }, [hours, minutes, seconds, debouncedHandleTimeChange])

  const setValue = (e: ChangeEvent<HTMLInputElement>, setterMethod: (value: string) => void) => {
    if (!e.target || isNaN(e.target.valueAsNumber) || e.target.valueAsNumber < 0) {
      return
    }
    setterMethod(e.target.value)
  }

  return (
    <div onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
      {label && <div className='label-small medium mb-2 zocks-text-gray-700'>{label}</div>}
      <div className={`time-picker p-2 border zocks-border-0.25 ${isFocused ? 'zocks-border-color-primary' : 'zocks-border-color-gray-500'} d-flex`}>
        <input disabled={disabled} type='number' className='border-0' value={hours} onChange={(e) => setValue(e, setHours)} />
        <span>:</span>
        <input disabled={disabled} type='number' className='border-0' value={minutes} onChange={(e) => setValue(e, setMinutes)} />
        <span>:</span>
        <input disabled={disabled} type='number' className='border-0' value={seconds} onChange={(e) => setValue(e, setSeconds)} />
      </div>
    </div>
  )
}
